import { useRef, useState, useEffect, useCallback } from "react";
import { RightOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import type { SiteInfo } from "@/components/UI/SitesMap/types";
import BarChartIcon from "@/assets/icons/bar_chart_4_bars.svg?react";
import SubjectsIcon from "@/assets/icons/subjects.svg?react";
import FlagIcon from "@/assets/icons/app_flag.svg?react";
import CalendarIcon from "@/assets/icons/calendar.svg?react";
import SitesIcon from "@/assets/icons/sites.svg?react";

const getDisplayValue = (value?: string | number | null) => {
  if (value === null || value === undefined || value === "" || value === "-")
    return "-";
  return String(value);
};

type LabelItemProps = {
  label: string;
  value: React.ReactNode;
};

const LabelItem = ({ label, value }: LabelItemProps) => (
  <div className="flex items-center gap-[6px] whitespace-nowrap">
    <span
      className="text-[14px] font-normal tracking-[-0.14px]"
      style={{ color: "rgba(0, 0, 0, 0.45)" }}
    >
      {label}
    </span>
    <span className="text-[14px] font-medium tracking-[-0.14px] text-[#2B4752]">
      {value ?? "-"}
    </span>
  </div>
);

type IconItemProps = {
  icon: React.ReactNode;
  value: React.ReactNode;
  tooltip?: string;
};

const IconItem = ({ icon, value, tooltip }: IconItemProps) => {
  const iconNode = (
    <span
      className="flex items-center shrink-0"
      style={{ color: "rgba(0, 0, 0, 0.45)" }}
    >
      {icon}
    </span>
  );
  return (
    <div className="flex items-center gap-1 whitespace-nowrap icon-small">
      {tooltip ? <Tooltip title={tooltip}>{iconNode}</Tooltip> : iconNode}
      <span
        className="text-[14px] font-normal tracking-[-0.14px]"
        style={{ color: "rgba(0, 0, 0, 0.85)", lineHeight: "24px" }}
      >
        {value ?? "-"}
      </span>
    </div>
  );
};

export default function SingleSiteCardHeader({
  siteInfo,
}: {
  siteInfo: SiteInfo | null;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [arrowDirection, setArrowDirection] = useState<
    "right" | "left" | "none"
  >("none");

  const checkOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const canScrollRight = el.scrollWidth > el.clientWidth + el.scrollLeft + 1;
    const canScrollLeft = el.scrollLeft > 1;
    if (canScrollRight) setArrowDirection("right");
    else if (canScrollLeft) setArrowDirection("left");
    else setArrowDirection("none");
  }, []);

  useEffect(() => {
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [checkOverflow]);

  const handleArrowClick = () => {
    isScrollingRef.current = true;
    clearTimeout(scrollTimeoutRef.current);
    if (arrowDirection === "right") {
      setArrowDirection("left");
      scrollRef.current?.scrollTo({
        left: scrollRef.current.scrollWidth,
        behavior: "smooth",
      });
    } else {
      setArrowDirection("right");
      scrollRef.current?.scrollTo({ left: 0, behavior: "smooth" });
    }
    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false;
    }, 500);
  };

  const handleScroll = () => {
    if (isScrollingRef.current) return;
    checkOverflow();
  };

  const info = siteInfo;

  return (
    <div
      className="flex h-[54px] p-3 justify-between items-center flex-1 text-[#2B4752]"
      style={{
        borderRadius: 8,
        border: "1px solid rgba(0, 0, 0, 0.04)",
        background: "#FFF",
        boxShadow: "0 2px 15px 4px rgba(0, 0, 0, 0.02)",
      }}
    >
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 min-w-0 overflow-x-auto hide-scrollbar flex items-center gap-5"
      >
        <LabelItem
          label="Site"
          value={
            info?.site_name
              ? `${info.site_name} (${info.site})`
              : getDisplayValue(info?.site)
          }
        />
        <LabelItem label="Country" value={getDisplayValue(info?.country)} />
        <LabelItem label="Region" value={getDisplayValue(info?.region)} />
        <LabelItem
          label="Investigator"
          value={getDisplayValue(info?.investigator_name)}
        />
        <IconItem
          icon={<CalendarIcon width={18} height={18} />}
          value={getDisplayValue(info?.date_activated)}
          tooltip="Activation Date"
        />
        <IconItem
          icon={<BarChartIcon width={18} height={18} />}
          value={info ? `${info.enrolled_count ?? 0} enrolled` : "-"}
          tooltip="Enrolled subjects"
        />
        <IconItem
          icon={<SubjectsIcon width={18} height={18} />}
          value={info ? `${info.subject_count ?? 0} total` : "-"}
          tooltip="Total subjects at site"
        />
        <IconItem
          icon={<FlagIcon width={18} height={18} />}
          value={getDisplayValue(info?.country_code)}
          tooltip="Country code"
        />
        <IconItem
          icon={<SitesIcon width={18} height={18} />}
          value={getDisplayValue(info?.address)}
          tooltip="Site address"
        />
      </div>

      {arrowDirection !== "none" && (
        <button
          onClick={handleArrowClick}
          className="flex items-center justify-center p-1 cursor-pointer border-none bg-transparent rounded-sm shrink-0"
          style={{ color: "rgba(0, 0, 0, 0.25)" }}
        >
          <RightOutlined
            style={{
              fontSize: 16,
              transform:
                arrowDirection === "left" ? "rotate(180deg)" : undefined,
              transition: "transform 0.2s ease",
            }}
          />
        </button>
      )}
    </div>
  );
}
